import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ChartOptions,
  ChartSeriesOptions,
  ExtensionName,
} from '@ct-software/ct-ui-chart';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AcquisitionChartColorService } from './acquisition-chart-colors.service';
import { ChannelConfig } from './channel-config.interface';
import { AcquisitionDataService } from './acquisition-data.service';
import { DatascribeChannelsHelper } from './channels-helper.service';
import { ACQ_TIME_CHANNEL } from './time-channel.const';
import { Channel } from './channel.interface';
import { DimensionData } from '@ct-software/ct-ui-core';
import { AcquisitionFile } from '../acquisition-chart/acquisition-file';
import { ACQUISITION_PANEL_DEFAULT_CHANNELS } from './acquisition-panel-default-channels';
import { WellKnownChannel, WellKnownChannels } from '@ct-software/ct-well-known-channels';

@UntilDestroy()
@Component({
    selector: 'acquisition-chart',
    templateUrl: './acquisition-chart.component.html',
    styleUrls: ['./acquisition-chart.component.scss'],
    standalone: false
})
export class AcquisitionChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public containers: string[];

  public chartOptions: ChartOptions = {} as ChartOptions;
  public notRenderedSeries: ChartSeriesOptions[] = [];
  public storedSeriesConfig: ChartSeriesOptions[] = [];
  public channels: ChannelConfig[] = [];
  public channelsCheckList: ChannelConfig[] = [];
  public currentChannels$: Observable<ChannelConfig[]>;
  public selectedChannels: ChannelConfig[] = [];
  public colors: Record<string, string> = {};
  public selectDefaultChannels: boolean = false;
  public channels$ = new BehaviorSubject<ChannelConfig[]>([]);
  public defaultChannels = ACQUISITION_PANEL_DEFAULT_CHANNELS;
  public chartCsv: string;

  private chartData$ = new BehaviorSubject<object[]>([]);
  private wellKnownChannels: WellKnownChannel[] = [];;

  constructor(
    public readonly acquisitionDataService: AcquisitionDataService,
    private readonly colorService: AcquisitionChartColorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly datascribeChannelsHelper: DatascribeChannelsHelper
  ) { }

  async ngOnInit() {
    this.wellKnownChannels = WellKnownChannels;
    this.acquisitionDataService.reset();
    this.acquisitionDataService.data$ = this.chartData$.asObservable();

    this.currentChannels$ = this.datascribeChannelsHelper.getCurrentRawWithLabelsChannels$(this.channels$.asObservable())
      .pipe(
        map((channels: Channel[]) => {
          return channels
            .filter((channel: Channel) => channel.name !== ACQ_TIME_CHANNEL)
            .sort((a, b) => (a.name < b.name ? -1 : 1))
        })
      );

    this.initChartOptions();
    this.initChannels();
  }

  public onUnitSystemChanged(unitSystem: string): void {
    this.loadData();
  }

  private loadData() {
    this.chartCsv = '';
    if (this.containers.length === 0) {
      return;
    }

    const loadingStartTime = Date.now();
    let loadingEndTime: number = null;
    let loaded = 0;
    let keepUpdating = true;

    const updateProgress = () => {
      if (!keepUpdating) {
        return;
      }

      const loadingSeconds = (((loadingEndTime || Date.now()) - loadingStartTime) / 1000).toFixed(1);
      const progressBar = '[' + '='.repeat(loaded) + ' '.repeat(this.containers.length - loaded) + ']';
      let progressMessage = `Loading...\n${progressBar} ${loadingSeconds} s`;
      if (loadingEndTime) {
        const convertingSeconds = ((Date.now() - loadingEndTime) / 1000).toFixed(1);
        progressMessage += `\nConverting...\n${convertingSeconds} s`;
      }

      this.setProgress(progressMessage);
      setTimeout(updateProgress, 100);
    }

    const promises = this.containers.map(
      x => this.acquisitionDataService.getDataForContainer(x)
        .then(x => {
          loaded++;
          return x;
        })
        .catch(err => {
          if (err.status !== 404) {
            throw err;
          }
        }));

    updateProgress();

    Promise.all(promises)
      .then(async files => {
        loadingEndTime = Date.now();
        const mergedFile = await this.convertAcquisitionFiles(files);
        keepUpdating = false;
        this.setProgress(null);
        this.setData(mergedFile);
      })
      .catch(err => {
        keepUpdating = false;

        this.setProgress('Error: ' + err);
      });
  }

  private convertAcquisitionFiles(files): Promise<AcquisitionFile> {
    return new Promise((resolve, reject) => {

      const worker = new Worker(new URL('./acquisition-chart.worker', import.meta.url));
      worker.onmessage = ({ data }) => {
        const mergedFile = Object.assign(new AcquisitionFile(), data);
        worker.terminate();
        resolve(mergedFile);
      };
      worker.postMessage([this.wellKnownChannels, ...files]);
    });
  }
  private setProgress(message: string) {
    let container = document.getElementById('progressContainer');
    if (message === null && container.innerHTML) {
      console.dir(container.innerHTML);
    }

    container.innerHTML = message;
  }

  private setData(file: AcquisitionFile) {
    let channels: ChannelConfig[] = [];
    for (const column of file.columns) {
      if (column.name === this.chartOptions.xAxis.propertyName) {
        continue;
      }

      const hasData = Math.abs(column.minValue - column.maxValue) > 0.001;
      if (!hasData) {
        continue;
      }

      channels.push({
        name: column.name,
        dimension: column.dimension,
        dimensionData: <DimensionData>{
          dimension: column.dimension,
          unit: column.dimension
        },

        selected: ['CT_WEIGHT', 'CIRC_PRESS', 'WH_PRESS', 'CT_CORR_DEPTH'].includes(column.name),
      })
    }

    channels.sort((a, b) => a.name < b.name ? -1 : 1);
    this.channels$.next(channels);
    this.channelListChanged();
    let res = file.parsedData
      .filter(item => Object.keys(item).length > 0)
      .map(item => {
        const updatedItem = {};
        Object.keys(item).forEach(key => { updatedItem[key] = Number.isNaN(item[key]) ? null : item[key]; });
        return updatedItem;
      });
    this.chartData$.next(res);
    this.acquisitionDataService.setInitialData(res);
    this.acquisitionDataService.forceUpdateChart();
    this.chartCsv = file.toCsv();
  }

  public ngOnChanges(): void {

    this.chartOptions.disableMinimumHeightToWidth = true;
    this.loadData();
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.acquisitionDataService.resetInitialData();
  }

  public channelListChanged(): void {
    this.updateChannelView();
    this.setSelectedChannels();
    this.setSeries();

    const colors = this.colorService.getColorsForChannels(this.chartOptions.series.map(x => x.propertyName));
    for (let i = 0; i < colors.length; i++) {
      this.chartOptions.series[i].color = colors[i];
    }

    this.chartOptions = Object.assign({}, this.chartOptions);
    this.acquisitionDataService.channels = this.selectedChannels;
  }

  public deselectAll(): void {
    this.channelListChanged();
  }

  public selectDefault(): void {
    this.channelListChanged();
  }

  private initChartOptions(): void {
    this.chartOptions = {
      xPropertyName: 'AcqTime',
      useSeparateYAxes: true,
      title: '',
      xAxis: { title: '', propertyName: 'AcqTime', isDate: true, dimension: null },
      yAxis: { title: '', dimension: null },
      series: [],
      extensions: this.getExtensions(),
      minimumXAxisRange: 5,
      disableMinimumHeightToWidth: true,
      hideGapsLargerThan: 120
    } as ChartOptions;
  }

  private isDefaultChannelsSelected(): boolean {
    const defaultChannels = ACQUISITION_PANEL_DEFAULT_CHANNELS;
    const selectedChannels = this.channelsCheckList
      .filter((channels) => channels.selected)
      .map((channels) => channels.name);

    return (
      selectedChannels.length === defaultChannels.length &&
      defaultChannels.every((channelName) =>
        selectedChannels.includes(channelName)
      )
    );
  }

  private getExtensions(): ExtensionName[] {
    return [
      ExtensionName.MultiAxis,
      ExtensionName.MultiAxisLegend,
      ExtensionName.SeparateTooltip
    ];
  }

  private updateChannelView(): void {
    this.channelsCheckList.forEach((channelConfig) => {
      this.channels.forEach((channel) => {
        if (channelConfig.name === channel.name) {
          channel.selected = channelConfig.selected;
        }
      });
    });

    this.selectDefaultChannels = this.isDefaultChannelsSelected();
    this.selectedChannels = this.channels.filter(channel => channel.selected);

    this.cdr.detectChanges();
  }

  private initChannels(): void {
    this.currentChannels$
      .pipe(
        untilDestroyed(this)
      ).subscribe((channels) => {

        this.channels = this.channelsCheckList = this.setChannelsCheckList(channels);

        this.selectedChannels = this.channels.filter(channel => channel.selected);
        this.selectDefaultChannels = this.isDefaultChannelsSelected();
        this.defineColors();
        this.setSelectedChannels();
      });
  }

  private setSelectedChannels(): void {
    this.chartOptions = Object.assign({}, this.chartOptions);

    this.cdr.detectChanges();
  }

  private defineColors(): void {
    const colors = this.colorService.getColorsForChannels(this.channels.map(x => x.name));

    this.channels
      .forEach((channel, index) => {
        this.colors[channel.name] = this.colors[channel.name] || colors[index];
      });

    this.cdr.detectChanges();
  }

  private setChannelsCheckList(channels: ChannelConfig[]): ChannelConfig[] {
    const defaultChannels = ACQUISITION_PANEL_DEFAULT_CHANNELS;

    return channels.map((channel) => {
      channel.selected = defaultChannels.includes(channel.name) ||
        this.channels.some(x => x.name === channel.name && x.selected);
      return channel;
    });
  }

  private setSeries(): void {
    this.chartOptions.series = this.channels.filter(x => x.selected).map(x => ({
      propertyName: x.name,
      color: undefined,
      dimension: <DimensionData>{
        dimension: x.dimension,
        unit: x.dimensionData.unit,
        format: x.dimensionData.format,
        unitLabel: x.label
      },
    } as ChartSeriesOptions));
  }
}
